// Dip : basket cross-sell rail engine (<dip-cart-crosssell>).
//
// One job: handle Add clicks on the rail's cards (snippets/dip-cart-crosssell.liquid).
// The shape is dip-buy-box.js #addToCart shrunk to one line item:
//   ONE POST to /cart/add.js with { items: [item], sections } so the Section
//   Rendering API returns the header's HTML, then a real CartAddEvent (which IS
//   the 'cart:update' DOM event) with sourceId 'dip-cart-crosssell'. From there:
//   - cart-items-component morphs the drawer from detail.data.sections
//   - dip-msc-guard sees a foreign sourceId and re-syncs gift codes
//   - cart-icon sets the bubble from detail.data.itemCount (absolute unit count)
//   - cart-drawer-component announces via its live region
//
// Guard-safety contract (docs/dip-gifting-playbook.md):
//   - subscription adds carry data-selling-plan resolved server-side (a one-time
//     add of a trigger/reward variant would NOT qualify for the gift codes)
//   - never dispatches a bare 'cart:update' without a sourceId
//   - never touches localStorage (dip:offers is published by buy surfaces only)
//   - never uses /discount/<code> URLs
//   - no optimistic DOM: all money re-renders from the server morph
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Read the basket's item count out of rendered section HTML.
 *
 * snippets/cart-bubble.liquid prints cart.item_count into [data-testid="cart-bubble"],
 * and every add already asks the Section Rendering API for the header, so the count
 * arrives in the SAME response as the drawer markup. Using it means an add is one
 * round trip: the drawer can open immediately instead of waiting on a second
 * GET /cart.js (which read as a lag, then a snap).
 *
 * Duplicated byte-identically in dip-buy-box.js, dip-home.js and dip-cart.js, the
 * same convention the .dip-reveal observer uses. Keep the three copies in sync.
 *
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
const dipCartCountFromSections = (sections) => {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-testid="cart-bubble"[^>]*>\s*([\d,]+)/);
    if (match) return parseInt(match[1].replace(/,/g, ''), 10);
  }
  return null;
};

class DipCartCrosssell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element && event.target.closest('.dip-cart-xs__add');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    // `_dip_src` (hidden line property, underscore = not rendered in the drawer or Recharge) records the
    // surface the add came from ('basket:<role>', printed by the card), so attribution can tell a basket
    // cross-sell add from a buy-box add. See dip-simple-buy.js. Never read by the theme or the cart guard.
    const props = { _dip_src: button.dataset.dipSrc || 'basket' };
    // Half now, half later (docs/dip-half-half-test.md): the arm and test id ride on every add, and an arm-B
    // subscription line carries the visible "Pay later" note (printed by the card in Liquid).
    const html = document.documentElement;
    const arm = html.getAttribute('data-dip-arm');
    if (arm === 'a' || arm === 'b') {
      props._dip_arm = arm;
      if (html.getAttribute('data-dip-test')) props._dip_test = html.getAttribute('data-dip-test');
    }
    if (html.hasAttribute('data-dip-hh') && button.dataset.sellingPlan && button.dataset.hhNote) {
      props[button.dataset.hhNoteName || 'Pay later'] = button.dataset.hhNote;
    }
    const item = { id: Number(button.dataset.variantId), quantity: 1, properties: props };
    if (button.dataset.sellingPlan) item.selling_plan = Number(button.dataset.sellingPlan);

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#error('');

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionIds.join(',') }),
      });
      const data = await res.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent('dip-cart-crosssell', data.message, data.description, data.errors));
        this.#error(data.description || data.message || 'Could not add that just now.');
        return;
      }

      // One round trip: the authoritative count is already in the section HTML we asked
      // for, so there is no second GET /cart.js on the critical path (see the helper).
      this.dispatchEvent(
        new CartAddEvent(undefined, 'dip-cart-crosssell', {
          source: 'dip-cart-crosssell',
          itemCount: dipCartCountFromSections(data.sections) ?? 1,
          variantId: String(item.id),
          sections: data.sections,
        })
      );
      // The morph replaces this subtree (the added product's card drops out of the
      // rail), so the finally block may mutate a detached node. Harmless.
    } catch (error) {
      console.warn('[dip-cart]', error);
      this.#error('Could not add that just now.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /** @param {string} text */
  #error(text) {
    const el = this.querySelector('.dip-cart-xs__err');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = text;
    el.hidden = !text;
  }
}

if (!customElements.get('dip-cart-crosssell')) {
  customElements.define('dip-cart-crosssell', DipCartCrosssell);
}

// Dip : "Spares & extras" rows (<dip-spares>, snippets/dip-spares.liquid, MSC PDP).
// Same add contract as the rail above: one POST with sections, then a real CartAddEvent.
// Spare products carry no selling plans and are separate from the kit hardware, so an add
// here can never change gift-code state (docs/dip-msc-spares-spec.md).
class DipSpares extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const step = target?.closest('[data-step]');
    if (step instanceof HTMLButtonElement) {
      const qtyEl = step.closest('.dip-spare-row')?.querySelector('[data-qty]');
      if (qtyEl) {
        const next = Math.min(9, Math.max(1, (Number(qtyEl.textContent) || 1) + Number(step.dataset.step)));
        qtyEl.textContent = String(next);
      }
      return;
    }
    const button = target?.closest('.dip-spare-add');
    if (button instanceof HTMLButtonElement && !button.disabled) this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const row = button.closest('.dip-spare-row');
    const quantity = Math.max(1, Number(row?.querySelector('[data-qty]')?.textContent) || 1);
    const item = { id: Number(button.dataset.variantId), quantity };

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#error('');

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionIds.join(',') }),
      });
      const data = await res.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent('dip-spares', data.message, data.description, data.errors));
        this.#error(data.description || data.message || 'Could not add that just now.');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(undefined, 'dip-spares', {
          source: 'dip-spares',
          itemCount: dipCartCountFromSections(data.sections) ?? quantity,
          variantId: String(item.id),
          sections: data.sections,
        })
      );
    } catch (error) {
      console.warn('[dip-spares]', error);
      this.#error('Could not add that just now.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /** @param {string} text */
  #error(text) {
    const el = this.querySelector('.dip-spares-err');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = text;
    el.hidden = !text;
  }
}

if (!customElements.get('dip-spares')) {
  customElements.define('dip-spares', DipSpares);
}

// ---- The pinned "Checkout securely" button (compact basket, docs/dip-basket-compact.md) ----
// Charlie 2026-09-30: "a little bit of glitching when I tap Checkout securely ... just as it loads there's some
// element overlap and shifting". Between the tap and checkout's first paint the basket stays on screen (longer on a
// phone), and as the page leaves, the express checkout buttons (PayPal, Shop Pay, Apple Pay iframes) are torn down.
// In the compact basket that block sits between the items and the totals, so when it collapsed the rest of the basket
// moved under the shopper's thumb. On the tap the basket's layout is frozen instead: the express block and the scroller
// keep their measured heights, and the button shows it is working and ignores a second tap. Restored if the shopper
// comes back with the browser's back button (the page is then served from the back-forward cache).
const FREEZE = '.dip-cart .additional-checkout-buttons, .dip-cart .cart-drawer__content';
document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('.dip-cart-pay__btn');
  if (!btn || btn.disabled) return;
  if (btn.classList.contains('is-going')) { e.preventDefault(); return; }
  document.querySelectorAll(FREEZE).forEach((el) => {
    el.style.height = `${el.getBoundingClientRect().height}px`;
    el.style.overflow = 'hidden';
  });
  btn.classList.add('is-going');
  btn.setAttribute('aria-busy', 'true');
});
window.addEventListener('pageshow', (e) => {
  if (!e.persisted) return;
  document.querySelectorAll(FREEZE).forEach((el) => { el.style.height = ''; el.style.overflow = ''; });
  document.querySelectorAll('.dip-cart-pay__btn.is-going').forEach((b) => { b.classList.remove('is-going'); b.removeAttribute('aria-busy'); });
});

// ---- Pinned or in line: the checkout button floats only when it has to (compact basket round 10) ----
// Charlie 2026-10-01: in a short basket the pinned button covered where Apple Pay shows, and moving the express buttons
// above the cross-sell hid the cross-sell. So the basket measures itself: when the summary's first row (in line, the
// checkout button itself, which leads the summary there: round 10b, "have it at the top when in line") would sit on the
// first screen WITHOUT the pinned row, the pinned row stands down and the summary's own checkout button (same label)
// shows in line; otherwise the pinned row stays. The summary starts at the same place in both modes, so the choice
// cannot flip back and forth. The class lives on <html>: the cart morph rewrites the drawer's own attributes.
// Styles: dip-chrome.css round 10.
const FIRST_ROW = 52; // the in-line checkout button (50px) plus a little air
const PAY_INLINE = 'dip-pay-inline';
const payObserved = new WeakSet();
let payRO = null;
function choosePayMode() {
  const root = document.documentElement;
  const sc = document.querySelector('cart-drawer-component .dip-cart .cart-drawer__content');
  const pay = document.querySelector('cart-drawer-component .dip-cart-pay');
  const summary = document.querySelector('cart-drawer-component .dip-cart .cart-drawer__summary');
  // Keep watching whatever the morph left in place (new nodes after a cart update).
  if (payRO) {
    document.querySelectorAll('cart-drawer-component .cart-drawer__content, cart-drawer-component .cart-drawer__items, cart-drawer-component .cart-drawer__summary')
      .forEach((el) => { if (!payObserved.has(el)) { payObserved.add(el); payRO.observe(el); } });
  }
  if (!sc || !pay || !summary || !sc.clientHeight) { root.classList.remove(PAY_INLINE); return; }
  const inline = root.classList.contains(PAY_INLINE);
  const room = sc.clientHeight + (inline ? 0 : pay.getBoundingClientRect().height);
  const padTop = parseFloat(getComputedStyle(summary).paddingTop) || 0;
  const top = summary.getBoundingClientRect().top + padTop - sc.getBoundingClientRect().top + sc.scrollTop;
  root.classList.toggle(PAY_INLINE, top + FIRST_ROW <= room);
}
let payModeRaf = 0;
const schedulePayMode = () => { cancelAnimationFrame(payModeRaf); payModeRaf = requestAnimationFrame(choosePayMode); };
function watchPayMode() {
  if (payRO) return;
  // Any change of size inside the drawer (opening, a line added or removed, images and express buttons loading, the
  // phone turning) re-decides; the mode switch itself resizes the scroller, which re-decides to the same answer.
  payRO = new ResizeObserver(schedulePayMode);
  // The drawer opening (its <dialog> gains `open`) and every cart update (after the morph lands).
  new MutationObserver(schedulePayMode).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
  document.addEventListener('cart:update', () => setTimeout(schedulePayMode, 60));
  window.addEventListener('resize', schedulePayMode);
  schedulePayMode();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchPayMode); else watchPayMode();
